import * as R from 'ramda';

import appData from '@/core/data';
import hoseOrientationsToAccessories from '../data/hose-orientations-to-accessories';

const defaultUnit = 'chirana-easy';

const propsMeta = {
	unit: {dataProp: 'units'},
	color: {dataProp: 'colors'},
	instrumentCount: {dataProp: 'instrumentCounts'},
	instruments: {isArray: true},
	instrumentTips: {isArray: true},
	instrumentsGroups: {dataProp: 'instrumentsGroups'},
	hoseOrientation: {dataProp: 'hoseOrientations'},
	chair: {dataProp: 'chairs'},
	chairAccessories: {isArray: true},
	physicianChairs: {dataProp: 'physicianChairs', isArray: true},
	footControl: {dataProp: 'footControls'},
	lamp: {dataProp: 'lamps'},
	lampOptions: {dataProp: 'lampOptions', isArray: true},
	monitor: {dataProp: 'monitors'},
	monitorOption: {dataProp: 'monitorOptions'},
	lampMastAccessory: {dataProp: 'lampMastAccessories'},
	lampMastAccessoriesAdditionalOptions: {
		dataProp: 'lampMastAccessoriesAdditionalOptions',
		isArray: true,
	},
	suctionSystem: {dataProp: 'suctionSystems'},
	waterSupplies: {dataProp: 'waterSupplies', isArray: true},
	extraColorOption: {dataProp: 'extraColorOptions'},
	addons: {dataProp: 'addons', isArray: true},
	physicianTableAccessories: {isArray: true},
	assistantTableType: {dataProp: 'assistantTableAccessories'},
	assistantTableAccessories: {isArray: true},
	hoseDeliverySystems: {isArray: true},
	discount: {dataProp: 'discount'},
};

const getData = state => prop => {
	const [meta, unit] = [propsMeta[prop], state.unit];
	if (meta) {
		const dataProp = meta.dataProp || prop;
		if (prop === 'unit') {
			return appData[dataProp]['' + state[prop]];
		}
		if (meta.isArray) {
			return state[prop].map(e => appData[dataProp][unit][e]);
		} else {
			return appData[dataProp][unit]['' + state[prop]];
		}
	}
};

const doesCurrentUnitSupportLampConfiguration = getters => !!getters.data('lamp');

export default {
	namespaced: true,
	state: appData.getDefaultConfigurationData(defaultUnit),

	getters: {
		data: getData,

		baseCost(state) {
			return appData.units[state.unit].cost + getData(state)('color').cost;
		},

		totalCost(state, getters) {
			let cost = getters.baseCost;
			for (const component of getters.costComponents) {
				cost += component[1];
			}

			return cost;
		},

		discount(state) {
			return state.discount;
		},

		totalCostWithDiscountIncluded(state, getters) {
			let totalCost = getters.totalCost;

			return Math.round((1 - getters.discount) * totalCost);
		},

		costComponents(state, getters) {
			let components = [],
				localCost;

			const instrumentCount = getters.data('instrumentCount');
			components.push([instrumentCount.fullName, instrumentCount.cost]);

			if (getters.data('hoseDeliverySystems').length > 0) {
				const systems = getters.data('hoseDeliverySystems');
				components.push([
					`${instrumentCount.name} x ${systems[0].fullName}`,
					instrumentCount.name * systems[0].cost,
				]);
			}

			for (let obj of getters.data('instruments').filter(i => !!i)) {
				components.push([
					obj.fullName,
					obj.cost,
					obj.isShownRegardlessOfCost ? 'showRegardlessOfCost' : null,
				]);
			}

			for (let obj of getters.data('instrumentTips')) {
				components.push([obj.fullName, obj.cost]);
			}

			for (let obj of getters.data('physicianTableAccessories')) {
				components.push([obj.fullName, obj.cost]);
			}

			if (state.suctionSystem || state.waterSupplies.length > 0) {
				if (state.suctionSystem) {
					components.push([
						getters.data('suctionSystem').fullName,
						getters.data('suctionSystem').cost,
						'showRegardlessOfCost',
					]);
				}
				const supplies = getters.data('waterSupplies') || [];
				supplies.forEach(obj => {
					components.push([obj.fullName, obj.cost]);
				});

				// components.push(['Wyposażenie bloku spluwaczki', localCost]);
			}

			const assistantTableType = getters.data('assistantTableType');
			if (assistantTableType) {
				components.push([assistantTableType.fullName, assistantTableType.cost]);
			}

			for (let obj of getters.data('assistantTableAccessories').filter(i => !!i)) {
				components.push([obj.fullName, obj.cost, 'showRegardlessOfCost']);
			}

			if (doesCurrentUnitSupportLampConfiguration(getters)) {
				const lamp = getters.data('lamp');

				const lampName = lamp.fullName;

				components.push([lampName, lamp.cost]);

				for (let obj of getters.data('lampOptions')) {
					components.push([obj.fullName, obj.cost, 'showRegardlessOfCost']);
				}

				const monitor = getters.data('monitor');
				components.push([monitor.fullName, monitor.cost]);

				const monitorOption = getters.data('monitorOption');
				if (monitorOption) {
					components.push([monitorOption.fullName, monitorOption.cost]);
				}

				const lampMastAccessory = getters.data('lampMastAccessory');
				components.push([lampMastAccessory.fullName, lampMastAccessory.cost]);

				const lampMastAccessoriesAdditionalOptions = getters.data(
					'lampMastAccessoriesAdditionalOptions'
				);
				for (const lampMastAccessoriesAdditionalOption of lampMastAccessoriesAdditionalOptions) {
					components.push([
						lampMastAccessoriesAdditionalOption.fullName,
						lampMastAccessoriesAdditionalOption.cost,
					]);
				}
			}

			const chair = getters.data('chair');
			if (chair) {
				components.push(['Fotel ' + getters.data('chair').fullName, chair.cost]);

				for (let obj of getters.data('physicianChairs')) {
					components.push([obj.fullName, obj.cost]);
				}

				if (state.chairAccessories.length > 0) {
					const chairAccessories = appData
						.getChairAccessories(getters.data('unit').name, chair.name)
						.filter(obj => state.chairAccessories.includes(obj.name));
					for (const obj of chairAccessories) {
						if (obj.isArmrestDependent) {
							const armrestCount = R.pipe(
								R.filter(R.propEq('isArmrest', true)),
								R.length,
								R.max(1)
							)(chairAccessories);
							components.push([`${armrestCount} x ${obj.fullName}`, armrestCount * obj.cost]);
						} else {
							components.push([obj.fullName, obj.cost]);
						}
					}
				}
			}

			for (let singleProp of ['footControl', 'extraColorOption']) {
				const value = getters.data(singleProp);
				if (value) {
					components.push([value.fullName, value.cost]);
				}
			}

			for (let obj of getters.data('addons').filter(i => !!i)) {
				components.push([obj.fullName, obj.cost]);
			}

			return components;
		},

		extras(state, getters) {
			return getters.costComponents.filter(([_, cost, behaviour]) => {
				return cost > 0 || behaviour === 'showRegardlessOfCost';
			});
		},

		extrasCost(state, getters) {
			return getters.extras.reduce((cost, e) => cost + e[1], 0);
		},
	},

	mutations: {
		setUnit(state, name) {
			Object.assign(state, appData.getDefaultConfigurationData(name));
		},

		setColor(state, name) {
			state.color = name;
		},

		setInstrumentCount(state, value) {
			const instruments =
				state.instrumentCount < value
					? R.concat(state.instruments, R.repeat(null, value - state.instrumentCount))
					: R.slice(0, value, state.instruments);
			Object.assign(state, {
				instrumentCount: value,
				instruments,
			});
			setPhysicianTableExtensions(state);
			setHoseDeliverySystems(state);
		},

		setInstruments(state, names) {
			state.instruments = R.slice(0, this.instrumentCount, names);
			setPhysicianTableExtensions(state);
		},

		setInstrumentTips(state, names) {
			state.instrumentTips = names;
		},

		setPhysicianTableAccessories(state, names) {
			state.physicianTableAccessories = names;
		},

		setAssistantTableType(state, name) {
			state.assistantTableType = name;
		},

		setAssistantTableAccessories(state, names) {
			const newAssistantTableAccessories = names;
			const selectedAccessoriesCount = R.pipe(
				R.slice(0, 4),
				R.filter(a => !!a),
				R.length
			)(names);

			if (
				selectedAccessoriesCount > 2 &&
				appData.assistantTableAccessories[state.unit]['ata-additional-slot']
			) {
				newAssistantTableAccessories[4] = 'ata-additional-slot';
			}
			state.assistantTableAccessories = newAssistantTableAccessories;
		},

		setChair(state, name) {
			Object.assign(state, {
				chair: name,
				chairAccessories: [],
			});
		},

		setChairAccessories(state, names) {
			state.chairAccessories = names;
		},

		setPhysicianChairs(state, names) {
			state.physicianChairs = names;
		},

		setFootControl(state, name) {
			state.footControl = name;
		},

		setHoseOrientation(state, value) {
			state.hoseOrientation = value;
			state.physicianTableAccessories = appData
				.getDefaultConfigurationProp(state.unit, 'physicianTableAccessories')
				.filter(pta =>
					hoseOrientationsToAccessories[state.unit][value].includes(pta.replace('pta-', ''))
				);

			setPhysicianTableExtensions(state);
			setHoseDeliverySystems(state);
		},

		setSuctionSystem(state, value) {
			if (['chirana-easy', 'chirana-l', 'chirana-effective'].includes(state.unit)) {
				if (
					[
						'suction-system-02',
						'suction-system-03',
						'suction-system-04',
						'suction-system-05',
					].includes(value)
				) {
					state.assistantTableAccessories[0] = 'ata-21';
					state.assistantTableAccessories[1] = 'ata-22';
				} else if ('suction-system-01' === value) {
					state.assistantTableAccessories[0] = 'ata-21';
					state.assistantTableAccessories[1] = null;
				}
			} else if ('chirana-uni' === state.unit) {
				if (value === 'suction-system-03') {
					state.assistantTableAccessories[0] = 'ata-21';
					state.assistantTableAccessories[1] = 'ata-22';
				} else {
					state.assistantTableAccessories[0] = null;
					state.assistantTableAccessories[1] = null;
				}
			}
			state.suctionSystem = value;
		},

		setWaterSupplies(state, names) {
			state.waterSupplies = names;
		},

		setExtraColorOption(state, value) {
			state.extraColorOption = value;
		},

		setAddons(state, value) {
			const addonAlreadyAdded = state.addons.find(option => option === value);

			if (addonAlreadyAdded) {
				state.addons = state.addons.filter(option => option !== value);
			} else {
				state.addons.push(value);
			}
		},

		setUnitConfiguration(state, newState) {
			Object.assign(state, newState);
		},

		setLamp(state, value) {
			state.lamp = value;
			state.lampMastAccessoriesAdditionalOptions = R.without(
				['lamp-mast-color'],
				state.lampMastAccessoriesAdditionalOptions
			);
		},

		setLampOptions(state, names) {
			state.lampOptions = names;
		},

		setMonitor(state, value) {
			state.monitor = value;
		},

		setMonitorOption(state, value) {
			state.monitorOption = value;
		},

		setLampMastAccessory(state, value) {
			state.lampMastAccessory = value;
		},

		setLampMastAccessoriesAdditionalOption(state, value) {
			const optionAlreadyAdded = state.lampMastAccessoriesAdditionalOptions.find(
				option => option === value
			);

			if (optionAlreadyAdded) {
				state.lampMastAccessoriesAdditionalOptions = state.lampMastAccessoriesAdditionalOptions.filter(
					option => option !== value
				);
			} else {
				state.lampMastAccessoriesAdditionalOptions.push(value);
			}
		},

		setDiscount(state, value) {
			state.discount = value;
		},
	},
};

const setHoseDeliverySystems = state => {
	state.hoseDeliverySystems =
		state.hoseOrientation === 'down'
			? []
			: new Array(state.instrumentCount).fill('hose-delivery-system-up');
};

const setPhysicianTableExtensions = state => {
	const newPhysicianTableAccessories = R.without(
		['ptex-1', 'ptex-2', 'ptex-3', 'ptex-4', 'ptex-5'],
		state.physicianTableAccessories
	);
	const waterBasedInstrumentCount = R.pipe(
		R.pluck('isWaterBased'),
		R.filter(x => x === true),
		R.length
	)(getData(state)('instruments'));

	if (state.instrumentCount === 6) {
		const extension = waterBasedInstrumentCount <= 3 ? 'ptex-3' : 'ptex-5';
		newPhysicianTableAccessories.push(extension);
	} else if (state.instrumentCount === 5) {
		const extension = `ptex-${waterBasedInstrumentCount}`;
		if (appData.physicianTableAccessories[state.unit][extension]) {
			newPhysicianTableAccessories.push(extension);
		}
	}

	state.physicianTableAccessories = newPhysicianTableAccessories;
};
